import type { Report } from '../../types';
import { projektIdVon, pruefeSchreiben, type SchreibSperre } from './bestandteileDialogLogic';

/**
 * DER GEBUNDENE SCHREIBWEG DER MODULE (Plan Paketposition B5, Codex R2b).
 *
 * Zwischen Klick und Gutschrift liegen Netzrunden (Ablage, Journal, RPC,
 * frisches Laden). Schliesst der Anwender in der Zeit das Modul oder wechselt
 * das Projekt, kaeme `onUpdate` mit dem Stand des ALTEN Projekts an — und
 * `App.handleUpdateReport` setzte ihn ueber den neuen. Jeder Schreibvorgang
 * eines Moduls wird deshalb beim START an sein Projekt gebunden und vor jedem
 * Schreiben geprueft: eingehaengt, der aktuelle Stand gehoert zu diesem
 * Projekt, der zu schreibende auch. Die Pruefung selbst ist D's
 * `pruefeSchreiben` (bestandteileDialogLogic) — eine Regel fuer Hub und
 * Module.
 *
 * Rein bis auf die hereingereichten Rueckrufe.
 */

export type { SchreibSperre };

/** Der Satz, wenn ein Schreiben verworfen wurde. `art` sagt, was liegen bleibt. */
export const verworfenText = (sperre: SchreibSperre, art: 'paket' | 'position'): string => {
    const warum = sperre === 'nicht_eingehaengt'
        ? 'Die Ansicht wurde inzwischen verlassen'
        : sperre === 'ohne_projekt'
            ? 'Das Projekt ist noch nicht gespeichert'
            : 'Das Projekt wurde inzwischen gewechselt';
    return art === 'paket'
        ? `${warum} — an der Paketposition wird hier nichts geschrieben; eine fällige Buchung holt der Nachtlauf nach.`
        : `${warum} — die Leistung wurde NICHT gebucht. Bitte im Projekt prüfen und bei Bedarf nachbuchen.`;
};

/**
 * Bindet einen Schreibvorgang an das Projekt `projektId` (beim Start
 * festgehalten). Die zurueckgegebene Funktion schreibt nur, wenn
 * `pruefeSchreiben` nichts einzuwenden hat, und liefert sonst den Grund —
 * geschrieben wird dann NICHTS.
 */
export const bindeSchreibweg = (a: {
    projektId: string;
    istEingehaengt: () => boolean;
    aktuell: () => Report | null;
    schreibe: (r: Report) => void;
    melde?: (sperre: SchreibSperre) => void;
}): ((r: Report) => SchreibSperre | null) => (r: Report) => {
    const sperre = pruefeSchreiben({ eingehaengt: a.istEingehaengt(), projektId: a.projektId, neu: r, aktuell: a.aktuell() });
    if (sperre) {
        a.melde?.(sperre);
        return sperre;
    }
    a.schreibe(r);
    return null;
};

/** Der Satz, wenn die gewaehlte Position waehrend des Exports verschwand. */
export const POSITION_FEHLT_TEXT =
    'Die gewählte Position ist nicht mehr im Projekt — die Leistung wurde NICHT gebucht. '
    + 'Bitte im Projekt prüfen und bei Bedarf nachbuchen.';

export interface ExportBuchungStand {
    /** Wurde die Buchung WIRKLICH geschrieben? Nur dann „gebucht" fuer Ablage und Meldung. */
    gebucht: boolean;
    /** Was der Anwender erfahren muss, wenn gewaehlt, aber nicht gebucht wurde. */
    hinweis: string | null;
}

/**
 * Was eine Exportbuchung (Vorankuendigung, UfsA, SiGe-Plan) ergeben hat —
 * rein (Codex R3). „Gebucht" ist nur, was der gebundene Schreibweg
 * angenommen hat; eine gewaehlte Position, die es nach dem Warten nicht mehr
 * gibt, ist KEINE Buchung und wird ausdruecklich gemeldet — sonst bekaeme die
 * Ablage „gebucht" mit und der Anwender erfuehre nichts.
 */
export const exportBuchungStand = (a: {
    gewaehlt: string | null | undefined;
    positionGefunden: boolean;
    verworfen: SchreibSperre | null;
}): ExportBuchungStand => {
    if (!(a.gewaehlt || '').trim()) return { gebucht: false, hinweis: null };
    if (!a.positionGefunden) return { gebucht: false, hinweis: POSITION_FEHLT_TEXT };
    if (a.verworfen) return { gebucht: false, hinweis: verworfenText(a.verworfen, 'position') };
    return { gebucht: true, hinweis: null };
};

/** Das Projekt eines Stands — fuer das Festhalten beim Start. */
export const projektDesVorgangs = (r: Report | null | undefined): string => projektIdVon(r);
